import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Typography } from "@mui/material";
import dayjs from "dayjs";
import StatusChip from "../../components/StatusChip";
import { OrderVM } from "../../data/types";
import { toOrderVM } from "../../data/mappers";
import { formatCAD } from "../../utils/currency";
import { fetchOrders } from "../../data/api";
import OrdersListTable, { OrderCol } from "../../orders/OrdersListTable";

const fmt = (d?: string) => (d ? dayjs(d).format("MMM D, YYYY") : "—");

/**
 * Distributor Orders list (Oct-10 QA #2). Already-working list is preserved and
 * now opens a DEDICATED detail page with a stable URL (/d/orders/:id) instead of
 * a dialog, so the URL changes and reload / Back / direct links work. Adds
 * status + date filtering, sorting and pagination.
 */
const OrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<OrderVM[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await fetchOrders() || []).map(toOrderVM)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const columns: OrderCol[] = [
    { header: "Order #", render: (o) => <Typography variant="body2" fontWeight={600} color="primary">{o.orderId}</Typography> },
    { header: "Customer", render: (o) => o.customer },
    { header: "Items", align: "right", render: (o) => o.items },
    { header: "Order Date", render: (o) => fmt(o.orderDate) },
    { header: "Delivery Date", render: (o) => fmt(o.deliveryDate) },
    { header: "Total (CAD)", align: "right", render: (o) => formatCAD(o.total) },
    { header: "Status", render: (o) => <StatusChip status={o.status} /> },
  ];

  return (
    <OrdersListTable
      title="Orders"
      rows={rows}
      loading={loading}
      columns={columns}
      onOpen={(o) => navigate(`/d/orders/${o.id}`)}
      empty="No orders yet."
    />
  );
};

export default OrdersPage;
